import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { getDefaultThemeForPersona, getThemesForPersona } from '@/utils/appThemes';
import { useActivePersona } from '@/personas/useActivePersona';

const STORAGE_KEY = 'tomorrow-theme';
const DEFAULT_THEME = 'forest';

export type ThemePreferenceContextType = {
  /**
   * Temat som FAKTISKT ska renderas — persona-upplöst, inte det råa lagrade valet.
   * Allt som målar färger (Layout, Navbar, FAB, teen-/barnvyerna, handledarvyerna)
   * läser detta och behöver därför inte känna till persona-regeln.
   */
  selectedTheme: string;
  setSelectedTheme: (themeId: string) => void;
  isDarkMode: boolean;
};

const ThemePreferenceContext = createContext<ThemePreferenceContextType>({
  selectedTheme: DEFAULT_THEME,
  setSelectedTheme: () => {},
  isDarkMode: false,
});

/**
 * ThemePreferenceProvider — EN sanning för vilket tema som gäller just nu.
 *
 * Temapaletterna är personabundna och har DISJUNKTA id:n (CHILD_THEMES/TEEN_THEMES/
 * ADULT_THEMES i appThemes.ts). Ett lagrat val tillhör alltså alltid exakt en persona,
 * och vid personabyte pekar `tomorrow-theme` i localStorage på en annan personas tema.
 * Regeln "tillhör temat inte personan → använd personans defaulttema" fanns tidigare
 * i App.tsx och i ThemeSelector, men INTE här — och därför inte i de ~20 komponenter
 * som målar via getThemeTokens(selectedTheme). Följden var att MUI-temat bytte färg vid
 * personabyte medan sidbakgrunden (Layout), navbaren och FAB:en låg kvar i den förra
 * vyns palett. Upplösningen bor nu på ETT ställe: här.
 *
 * Det RÅA valet skrivs medvetet inte om i localStorage — byter brukaren tillbaka
 * persona kommer hens tema tillbaka.
 */
export const ThemePreferenceProvider = ({ children }: { children: React.ReactNode }) => {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  // Providern ligger utanför SupervisorScene → inga options, hooken faller tillbaka
  // på sessionStorage/localStorage (som SupervisorContext håller i synk och vars
  // ändringar dispatchar 'storage'). Samma källval som App.tsx alltid använt.
  const profile = useActivePersona();

  const [storedTheme, setStoredTheme] = useState<string>(
    () => localStorage.getItem(STORAGE_KEY) || DEFAULT_THEME,
  );

  const [darkModeSetting, setDarkModeSetting] = useState<string | null>(() =>
    localStorage.getItem('darkMode'),
  );

  const isDarkMode = useMemo(() => {
    if (darkModeSetting === 'dark') return true;
    if (darkModeSetting === 'light') return false;
    return prefersDarkMode;
  }, [darkModeSetting, prefersDarkMode]);

  const selectedTheme = useMemo(() => {
    const selectableIds = getThemesForPersona(profile.persona).map((theme) => theme.id);
    return selectableIds.includes(storedTheme)
      ? storedTheme
      : getDefaultThemeForPersona(profile.persona);
  }, [storedTheme, profile.persona]);

  const setSelectedTheme = useCallback((themeId: string) => {
    setStoredTheme(themeId);
    localStorage.setItem(STORAGE_KEY, themeId);
    window.dispatchEvent(new Event('storage'));
  }, []);

  useEffect(() => {
    const handleStorage = () => {
      setStoredTheme(localStorage.getItem(STORAGE_KEY) || DEFAULT_THEME);
      setDarkModeSetting(localStorage.getItem('darkMode'));
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const value = useMemo(
    () => ({ selectedTheme, setSelectedTheme, isDarkMode }),
    [selectedTheme, setSelectedTheme, isDarkMode],
  );

  return (
    <ThemePreferenceContext.Provider value={value}>{children}</ThemePreferenceContext.Provider>
  );
};

export const useThemePreference = (): ThemePreferenceContextType => {
  return useContext(ThemePreferenceContext);
};
